import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Fingerprint, Clock, CheckCircle, LogIn, LogOut, AlertCircle, MapPin, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimeClock = () => {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [phtTime, setPhtTime] = useState(new Date());
  const [todayEntry, setTodayEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [clockAction, setClockAction] = useState('in'); // 'in' or 'out'
  const [fingerprintSupported, setFingerprintSupported] = useState(false);
  const [fingerprintRegistered, setFingerprintRegistered] = useState(false);
  
  // Location state
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationSettings, setLocationSettings] = useState(null);

  // Update clock every second - show PHT (Philippine Time, UTC+8)
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(now);
      // Calculate Philippine Time (UTC+8)
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      const pht = new Date(utc + (8 * 60 * 60000));
      setPhtTime(pht);
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  // Check fingerprint support
  useEffect(() => {
    checkFingerprintSupport();
  }, []);

  // Fetch today's entry
  useEffect(() => {
    fetchTodayEntry();
    fetchLocationSettings();
  }, []);

  // Get current location on mount
  useEffect(() => {
    getCurrentLocation();
  }, []);

  const fetchLocationSettings = async () => {
    try {
      const res = await axios.get(`${API}/offices/employee/${user?.id}/settings`);
      setLocationSettings(res.data);
    } catch (error) {
      // User may not have location settings configured
      setLocationSettings(null);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported by your browser');
      return;
    }

    setGettingLocation(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGettingLocation(false);
      },
      (error) => {
        let errorMsg = 'Failed to get location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Location access denied. Please enable GPS.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Location unavailable. Please try again.';
            break;
          case error.TIMEOUT:
            errorMsg = 'Location request timed out.';
            break;
        }
        setLocationError(errorMsg);
        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000
      }
    );
  };

  const checkFingerprintSupport = async () => {
    try {
      if (window.PublicKeyCredential) {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setFingerprintSupported(available);
        
        // Check if user has registered fingerprint
        if (available && user?.id) {
          try {
            const res = await axios.get(`${API}/timeclock/fingerprint-status`);
            setFingerprintRegistered(res.data?.registered || false);
          } catch (e) {
            setFingerprintRegistered(false);
          }
        }
      }
    } catch (e) {
      setFingerprintSupported(false);
    }
  };

  const fetchTodayEntry = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/timeclock/today`);
      setTodayEntry(res.data);
    } catch (error) {
      console.error('Error fetching today entry:', error);
    }
    setLoading(false);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    });
  };

  const formatPhtDate = (date) => {
    // Format the PHT date
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getGreeting = () => {
    // Use PHT time for greeting
    const hour = phtTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Register fingerprint
  const registerFingerprint = async () => {
    setProcessing(true);
    try {
      // Get challenge from server
      const challengeRes = await axios.post(`${API}/timeclock/fingerprint-register-challenge`);
      const { challenge, userId } = challengeRes.data;

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: Uint8Array.from(atob(challenge), c => c.charCodeAt(0)),
          rp: {
            name: "ARBE Time Clock",
            id: window.location.hostname
          },
          user: {
            id: Uint8Array.from(userId, c => c.charCodeAt(0)),
            name: user?.username || 'user',
            displayName: user?.full_name || user?.username || 'User'
          },
          pubKeyCredParams: [
            { type: "public-key", alg: -7 },  // ES256
            { type: "public-key", alg: -257 } // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            userVerification: "required"
          },
          timeout: 60000
        }
      });

      // Send credential to server
      const credentialData = {
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
        type: credential.type,
        response: {
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))),
          attestationObject: btoa(String.fromCharCode(...new Uint8Array(credential.response.attestationObject)))
        }
      };

      await axios.post(`${API}/timeclock/fingerprint-register`, credentialData);
      toast.success('Fingerprint registered successfully!');
      setFingerprintRegistered(true);
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error('Fingerprint registration cancelled');
      } else {
        toast.error('Failed to register fingerprint');
        console.error(error);
      }
    }
    setProcessing(false);
  };

  // Authenticate with fingerprint
  const authenticateFingerprint = async () => {
    setProcessing(true);
    try {
      // Get challenge from server
      const challengeRes = await axios.post(`${API}/timeclock/fingerprint-auth-challenge`);
      const { challenge, credentialIds } = challengeRes.data;

      // Get credential
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: Uint8Array.from(atob(challenge), c => c.charCodeAt(0)),
          allowCredentials: credentialIds.map(id => ({
            type: "public-key",
            id: Uint8Array.from(atob(id), c => c.charCodeAt(0))
          })),
          userVerification: "required",
          timeout: 60000
        }
      });

      // Verify with server and clock in/out
      const assertionData = {
        id: assertion.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(assertion.rawId))),
        type: assertion.type,
        response: {
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(assertion.response.clientDataJSON))),
          authenticatorData: btoa(String.fromCharCode(...new Uint8Array(assertion.response.authenticatorData))),
          signature: btoa(String.fromCharCode(...new Uint8Array(assertion.response.signature)))
        }
      };

      await axios.post(`${API}/timeclock/${clockAction}`, {
        method: 'fingerprint',
        assertion: assertionData,
        latitude: location?.latitude,
        longitude: location?.longitude
      });

      toast.success(`Time ${clockAction === 'in' ? 'In' : 'Out'} recorded with fingerprint!`);
      fetchTodayEntry();
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error('Fingerprint authentication cancelled');
      } else if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Location verification failed');
      } else {
        toast.error('Fingerprint authentication failed');
        console.error(error);
      }
    }
    setProcessing(false);
  };

  // Manual clock in/out
  const handleManualClock = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/timeclock/${clockAction}`, {
        method: 'manual',
        latitude: location?.latitude,
        longitude: location?.longitude
      });
      toast.success(`Time ${clockAction === 'in' ? 'In' : 'Out'} recorded!`);
      setShowConfirmDialog(false);
      fetchTodayEntry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record time');
    }
    setProcessing(false);
  };

  // Handle fingerprint clock
  const handleFingerprintClock = async (action) => {
    setClockAction(action);
    if (fingerprintRegistered) {
      await authenticateFingerprint();
    } else {
      toast.error('Please register your fingerprint first');
    }
  };

  // Handle manual clock button
  const handleManualClockButton = (action) => {
    setClockAction(action);
    setShowConfirmDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const canClockIn = !todayEntry?.clock_in_time;
  const canClockOut = todayEntry?.clock_in_time && !todayEntry?.clock_out_time;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6">
      <div className="max-w-lg mx-auto space-y-6">
        
        {/* Page Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center justify-center gap-2">
            <Clock className="text-blue-600" size={28} />
            Time In / Out
          </h1>
          <p className="text-slate-500 text-sm mt-1">Clock your attendance</p>
        </div>
        
        {/* Greeting & Time - Philippine Time (UTC+8) */}
        <Card className="bg-white shadow-xl">
          <CardContent className="pt-8 pb-6 text-center">
            <p className="text-slate-500 text-lg">{getGreeting()},</p>
            <h1 className="text-3xl font-bold text-slate-800 mt-1">
              {user?.full_name || user?.username}
            </h1>
            
            <div className="mt-6">
              <p className="text-5xl font-mono font-bold text-blue-600">
                {formatTime(phtTime)}
              </p>
              <p className="text-slate-500 mt-2">{formatPhtDate(phtTime)}</p>
              {/* PHT Timezone Indicator */}
              <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-full border border-blue-200">
                <span className="text-lg">🇵🇭</span>
                <span className="text-sm font-medium text-blue-700">Philippine Time (PHT)</span>
                <span className="text-xs text-blue-500 ml-1">UTC+8</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Today's Status */}
        <Card className="bg-white shadow-lg">
          <CardContent className="pt-4">
            <h2 className="text-lg font-semibold text-slate-700 mb-3">Today's Record</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg ${todayEntry?.clock_in_time ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}>
                <p className="text-sm text-slate-500">Time In</p>
                <p className={`text-xl font-bold ${todayEntry?.clock_in_time ? 'text-green-600' : 'text-slate-400'}`}>
                  {todayEntry?.clock_in_time || '--:--'}
                </p>
                {todayEntry?.clock_in_method && (
                  <p className="text-xs text-slate-400 mt-1">
                    via {todayEntry.clock_in_method === 'fingerprint' ? '👆 Fingerprint' : '✋ Manual'}
                  </p>
                )}
              </div>
              <div className={`p-4 rounded-lg ${todayEntry?.clock_out_time ? 'bg-red-50 border border-red-200' : 'bg-slate-50'}`}>
                <p className="text-sm text-slate-500">Time Out</p>
                <p className={`text-xl font-bold ${todayEntry?.clock_out_time ? 'text-red-600' : 'text-slate-400'}`}>
                  {todayEntry?.clock_out_time || '--:--'}
                </p>
                {todayEntry?.clock_out_method && (
                  <p className="text-xs text-slate-400 mt-1">
                    via {todayEntry.clock_out_method === 'fingerprint' ? '👆 Fingerprint' : '✋ Manual'}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location Status */}
        {locationSettings?.assigned_office && (
          <Card className={`shadow-lg ${locationError ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPin className={`h-5 w-5 ${location ? 'text-green-500' : 'text-slate-400'}`} />
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      {locationSettings.assigned_office.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      Radius: {locationSettings.clock_in_radius_km || 2}km
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {gettingLocation ? (
                    <p className="text-xs text-blue-500 animate-pulse">Getting location...</p>
                  ) : locationError ? (
                    <div>
                      <p className="text-xs text-red-500">{locationError}</p>
                      <Button variant="ghost" size="sm" onClick={getCurrentLocation} className="text-xs">
                        <Navigation className="h-3 w-3 mr-1" /> Retry
                      </Button>
                    </div>
                  ) : location ? (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Location ready
                    </p>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {locationSettings?.allow_remote_clock && (
          <div className="text-center text-sm text-green-600 bg-green-50 py-2 px-4 rounded-lg">
            <CheckCircle className="h-4 w-4 inline mr-1" />
            Remote clock-in enabled
          </div>
        )}

        {/* Clock In/Out Buttons */}
        {(canClockIn || canClockOut) && (
          <div className="space-y-4">
            {/* Fingerprint Option */}
            {fingerprintSupported && (
              <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-xl">
                <CardContent className="pt-6 pb-6">
                  {!fingerprintRegistered ? (
                    <div className="text-center">
                      <Fingerprint className="h-12 w-12 mx-auto mb-3 opacity-80" />
                      <p className="mb-4">Register your fingerprint for quick clock in/out</p>
                      <Button 
                        onClick={registerFingerprint} 
                        disabled={processing}
                        className="bg-white text-blue-600 hover:bg-blue-50"
                      >
                        {processing ? 'Registering...' : 'Register Fingerprint'}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Fingerprint className="h-16 w-16 mx-auto mb-4" />
                      <p className="text-lg mb-4">Tap to {canClockIn ? 'Time In' : 'Time Out'} with Fingerprint</p>
                      <Button
                        size="lg"
                        onClick={() => handleFingerprintClock(canClockIn ? 'in' : 'out')}
                        disabled={processing}
                        className={`w-full text-lg py-6 ${
                          canClockIn 
                            ? 'bg-green-500 hover:bg-green-600' 
                            : 'bg-red-500 hover:bg-red-600'
                        }`}
                      >
                        {processing ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                            Verifying...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2 justify-center">
                            <Fingerprint className="h-6 w-6" />
                            {canClockIn ? 'TIME IN' : 'TIME OUT'}
                          </span>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Divider */}
            {fingerprintSupported && fingerprintRegistered && (
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-slate-300"></div>
                <span className="text-slate-400 text-sm">OR</span>
                <div className="flex-1 h-px bg-slate-300"></div>
              </div>
            )}

            {/* Manual Option */}
            <Card className="bg-white shadow-lg">
              <CardContent className="pt-6 pb-6">
                <div className="text-center">
                  <Clock className="h-10 w-10 mx-auto mb-3 text-slate-400" />
                  <p className="text-slate-600 mb-4">Manual Time {canClockIn ? 'In' : 'Out'}</p>
                  <Button
                    size="lg"
                    onClick={() => handleManualClockButton(canClockIn ? 'in' : 'out')}
                    disabled={processing}
                    className={`w-full text-lg py-6 ${
                      canClockIn 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {canClockIn ? (
                      <span className="flex items-center gap-2 justify-center">
                        <LogIn className="h-6 w-6" />
                        MANUAL TIME IN
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 justify-center">
                        <LogOut className="h-6 w-6" />
                        MANUAL TIME OUT
                      </span>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Already Clocked Out */}
        {!canClockIn && !canClockOut && todayEntry?.clock_out_time && (
          <Card className="bg-green-50 border-green-200 shadow-lg">
            <CardContent className="pt-6 pb-6 text-center">
              <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <h2 className="text-xl font-bold text-green-700">All Done for Today!</h2>
              <p className="text-green-600 mt-2">
                You worked from {todayEntry.clock_in_time} to {todayEntry.clock_out_time}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Fingerprint not supported warning */}
        {!fingerprintSupported && (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">Fingerprint not available on this device. Use manual time in/out.</p>
          </div>
        )}
      </div>

      {/* Manual Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {clockAction === 'in' ? <LogIn className="text-green-500" /> : <LogOut className="text-red-500" />}
              Confirm Time {clockAction === 'in' ? 'In' : 'Out'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <p className="text-4xl font-mono font-bold text-slate-700">
              {formatTime(phtTime)}
            </p>
            <div className="flex items-center justify-center gap-1 mt-2 text-blue-600">
              <span className="text-sm">🇵🇭</span>
              <span className="text-xs font-medium">Philippine Time (PHT)</span>
            </div>
            <p className="text-slate-500 mt-3">
              Are you sure you want to {clockAction === 'in' ? 'clock in' : 'clock out'} now?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleManualClock}
              disabled={processing}
              className={clockAction === 'in' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
            >
              {processing ? 'Recording...' : `Confirm Time ${clockAction === 'in' ? 'In' : 'Out'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeClock;
